import React, { useState } from 'react';

/**
 * TagsManager Component
 * 
 * Enterprise-style tag management component
 * Supports adding, removing, and color-coding tags
 */
const TagsManager = ({ tags = [], onTagsChange, predefinedTags = [], readOnly = false }) => {
  const [newTag, setNewTag] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Default predefined tags if none provided
  const defaultTags = predefinedTags.length > 0 ? predefinedTags : [
    'Strong Candidate',
    'Culture Fit',
    'Technical Excellence',
    'High Priority',
    'Needs Review',
    'Overqualified',
    'Underqualified',
    'Referred'
  ];

  const tagColors = {
    'Strong Candidate': { bg: '#D1FAE5', text: '#065F46', border: '#6EE7B7' },
    'Culture Fit': { bg: '#DBEAFE', text: '#1E3A8A', border: '#93C5FD' },
    'Technical Excellence': { bg: '#FEF3C7', text: '#92400E', border: '#FDE68A' },
    'High Priority': { bg: '#FEE2E2', text: '#991B1B', border: '#FCA5A5' },
    'Needs Review': { bg: '#E0E7FF', text: '#3730A3', border: '#C7D2FE' },
    'Overqualified': { bg: '#F3F4F6', text: '#374151', border: '#D1D5DB' },
    'Underqualified': { bg: '#FEF2F2', text: '#991B1B', border: '#FECACA' },
    'Referred': { bg: '#ECFDF5', text: '#047857', border: '#A7F3D0' }
  };

  const getTagStyle = (tag) => {
    const colorScheme = tagColors[tag] || { bg: '#F3F4F6', text: '#374151', border: '#D1D5DB' };
    return {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '0.375rem 0.75rem',
      borderRadius: '6px',
      fontSize: '0.8125rem',
      fontWeight: '500',
      backgroundColor: colorScheme.bg,
      color: colorScheme.text,
      border: `1px solid ${colorScheme.border}`,
      marginRight: '0.5rem',
      marginBottom: '0.5rem'
    };
  };

  const handleAddTag = (tag) => {
    const tagToAdd = tag || newTag.trim();
    if (!tagToAdd || tags.includes(tagToAdd)) {
      setNewTag('');
      setShowSuggestions(false);
      return;
    }

    const newTags = [...tags, tagToAdd];
    if (onTagsChange) {
      onTagsChange(newTags);
    }
    setNewTag('');
    setShowSuggestions(false);
  };

  const handleRemoveTag = (tagToRemove) => {
    if (readOnly) return;
    const newTags = tags.filter(tag => tag !== tagToRemove);
    if (onTagsChange) {
      onTagsChange(newTags);
    }
  };

  const availableTags = defaultTags.filter(tag => !tags.includes(tag));

  return (
    <div style={{ padding: '1rem 0' }}>
      {/* Display existing tags */}
      {tags.length > 0 && (
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          marginBottom: readOnly ? '0' : '1rem'
        }}>
          {tags.map(tag => (
            <span key={tag} style={getTagStyle(tag)}>
              {tag}
              {!readOnly && (
                <button
                  onClick={() => handleRemoveTag(tag)}
                  style={{
                    marginLeft: '0.5rem',
                    background: 'none',
                    border: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    lineHeight: '1',
                    padding: '0',
                    opacity: '0.7'
                  }}
                  onMouseEnter={(e) => e.target.style.opacity = '1'}
                  onMouseLeave={(e) => e.target.style.opacity = '0.7'}
                  title="Remove tag"
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Add new tag input */}
      {!readOnly && (
        <div style={{ position: 'relative' }}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              type="text"
              value={newTag}
              onChange={(e) => {
                setNewTag(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddTag();
                }
              }}
              placeholder="Add tag or select from suggestions..."
              style={{
                flex: 1,
                padding: '0.5rem 0.75rem',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '0.875rem',
                outline: 'none'
              }}
              onBlur={() => {
                // Delay to allow click on suggestions
                setTimeout(() => setShowSuggestions(false), 200);
              }}
            />
            <button
              onClick={() => handleAddTag()}
              disabled={!newTag.trim()}
              style={{
                padding: '0.5rem 1rem',
                background: newTag.trim() ? '#1E40AF' : '#9CA3AF',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: newTag.trim() ? 'pointer' : 'not-allowed',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              Add
            </button>
          </div>

          {/* Suggestions dropdown */}
          {showSuggestions && availableTags.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: '0.25rem',
              background: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              zIndex: 1000,
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              {availableTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => handleAddTag(tag)}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    border: 'none',
                    background: 'white',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    color: '#374151'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#F9FAFB'}
                  onMouseLeave={(e) => e.target.style.background = 'white'}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {tags.length === 0 && readOnly && (
        <p style={{ fontSize: '0.875rem', color: '#9CA3AF', fontStyle: 'italic' }}>
          No tags added yet
        </p>
      )}
    </div>
  );
};

export default TagsManager;
